import React, { useState } from 'react';
import { stackApp } from '../../stackApp';
import { publicAssetUrl } from '../../lib/publicAssetUrl';

const GOOGLE_ICON = (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
    <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.7 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.8 6.5 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-4z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.1 18.9 12 24 12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.8 6.5 29.2 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5.1l-6.2-5.2C29.4 35.5 26.8 36 24 36c-5.2 0-9.6-2.9-11.3-7l-6.6 5.1C9.8 39.7 16.4 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.5l6.2 5.2C41 35.3 44 30 44 24c0-1.3-.1-2.7-.4-4z"/>
  </svg>
);

function SignInPanel({ onGuest }) {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'signin') {
        await stackApp.signInWithCredential({ email, password });
      } else {
        await stackApp.signUpWithCredential({ email, password, displayName: displayName || undefined });
      }
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text)',
    fontSize: 13,
    outline: 'none',
  };

  return (
    <div style={{
      width: 280,
      flexShrink: 0,
      borderLeft: '1px solid var(--border)',
      paddingLeft: 28,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          {mode === 'signin' ? 'Sign in' : 'Create account'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Sync your saves across devices
        </div>
      </div>

      {/* Google */}
      <button
        className="btn btn-secondary"
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '9px 12px', marginBottom: 16 }}
        onClick={() => stackApp && stackApp.signInWithOAuth('google')}
      >
        {GOOGLE_ICON}
        Continue with Google
      </button>

      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>or</span>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>

      {/* Email/password form */}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {mode === 'signup' && (
          <input
            style={inputStyle}
            type="text"
            placeholder="Display name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
          />
        )}
        <input
          style={inputStyle}
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />
        <input
          style={inputStyle}
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />
        {error && (
          <div style={{ fontSize: 11, color: 'var(--red)', lineHeight: 1.4 }}>{error}</div>
        )}
        <button
          className="btn btn-primary"
          type="submit"
          disabled={loading}
          style={{ width: '100%', padding: '9px', marginTop: 2 }}
        >
          {loading ? '...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
        </button>
      </form>

      {/* Toggle mode */}
      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
        {mode === 'signin' ? (
          <>No account?{' '}
            <button onClick={() => { setMode('signup'); setError(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: 12, padding: 0 }}>
              Create one
            </button>
          </>
        ) : (
          <>Have an account?{' '}
            <button onClick={() => { setMode('signin'); setError(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: 12, padding: 0 }}>
              Sign in
            </button>
          </>
        )}
      </div>

      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0' }}>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>or</span>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>

      {/* Guest */}
      <button
        className="btn btn-secondary"
        style={{ width: '100%', padding: '9px', fontSize: 13 }}
        onClick={onGuest}
      >
        Play as Guest
      </button>
      <div style={{ fontSize: 10, color: 'var(--text-dim)', textAlign: 'center', marginTop: 8, lineHeight: 1.4 }}>
        Saves stored locally — you can sign in later
      </div>
    </div>
  );
}

export function WelcomeModal({ dispatch, user }) {
  const startTutorial = () => dispatch({ type: 'TUTORIAL_DISMISS_WELCOME' });
  const skipTutorial = () => dispatch({ type: 'TUTORIAL_SKIP' });
  const signedIn = !!user;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        width: '100%',
        maxWidth: signedIn ? 480 : 760,
        display: 'flex',
        gap: 0,
        overflow: 'hidden',
        maxHeight: '90vh',
        overflowY: 'auto',
        transition: 'max-width 0.3s ease',
      }}>
        {/* Left — welcome content */}
        <div style={{ flex: 1, padding: '32px 28px' }}>
          <div style={{ maxWidth: 300, margin: '0 auto 20px', display: 'flex', justifyContent: 'center' }}>
            <img
              src={publicAssetUrl('assets/logo.png')}
              alt="CannTycoon"
              style={{ width: '100%', height: 'auto', maxHeight: 140, objectFit: 'contain', display: 'block' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>

          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 20 }}>
            You're running a cannabis grow operation. Keep plants healthy, manage your team,
            and turn a profit before cash runs out.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            {[
              { icon: '🌱', title: 'Grow', desc: 'Plant batches, control temperature and humidity, and guide your crop through each stage.' },
              { icon: '💵', title: 'Sell', desc: 'Harvest quality product and sell through wholesale, medical, or retail channels.' },
              { icon: '📈', title: 'Expand', desc: 'Unlock new rooms, research technology, and build your reputation to reach Stage 2+.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 12px', background: 'var(--border-light)', borderRadius: 'var(--radius-sm)' }}>
                <span style={{ fontSize: 20 }}>{icon}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {signedIn ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 14px',
              background: 'var(--green-dark)',
              border: '1px solid var(--green-dim)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: 20,
            }}>
              <span style={{ fontSize: 20 }}>☁</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--green)', marginBottom: 2 }}>
                  Signed in as {user.displayName || user.primaryEmail}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Your progress will sync to the cloud automatically.
                </div>
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.4, marginBottom: 20 }}>
              The tutorial guides you through your first grow cycle. You can skip it at any time.
            </p>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={skipTutorial}>
              Skip Tutorial
            </button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={startTutorial}>
              Start Tutorial →
            </button>
          </div>
        </div>

        {/* Right — sign in panel or success state */}
        {signedIn ? (
          <div style={{
            width: 0, overflow: 'hidden', transition: 'width 0.3s ease',
          }} />
        ) : (
          <div style={{ padding: '32px 28px', display: 'flex', alignItems: 'flex-start' }}>
            <SignInPanel onGuest={startTutorial} />
          </div>
        )}
      </div>
    </div>
  );
}
